<template>
    <ModalContent
        flat-chrome
        :help-content-id="`popup-customer-${current_entity_profile}`"
    >
        <template slot="title">{{ title }}</template>
        <template slot="content" v-if="customer !== null">
            <div class="stitch-modal general-settings-reference stitch-form">
                <Warning :elevation="2" v-if="isExternal">
                    {{ $translate("instructionsExternal") }}
                </Warning>

                <p
                    v-if="showRequiredFeedback"
                    class="required-fields-legend mb-4"
                >
                    {{ $translate("requiredFieldsLegend") }}
                </p>

                <v-card
                    class="stitch-modal__card customer-configuration-card"
                    elevation="0"
                >
                    <v-card-text>
                        <h5 class="stitch-modal__title">
                            <v-icon size="16">mdi-account-cog-outline</v-icon>
                            <span>{{
                                $translate("customerConfiguration")
                            }}</span>
                        </h5>
                        <v-row dense class="stitch-modal__fields">
                            <v-col cols="12" sm="4">
                                <CompanySearchAutocomplete
                                    v-if="
                                        customer.type == 'company' ||
                                        !customer.type
                                    "
                                    v-model="customer.name"
                                    :error-messages="nameErrorMessages"
                                    :label="$translate('labels.name')"
                                    :placeholder="
                                        $translate('placeholders.companyName')
                                    "
                                    @company-selected="onCompanySelected"
                                    :dense="true"
                                    outlined
                                    @keydown="handleKeyPress"
                                    :autofocus="true"
                                />
                                <v-text-field
                                    v-else
                                    v-model="customer.name"
                                    :error-messages="nameErrorMessages"
                                    :label="$translate('labels.name')"
                                    :placeholder="
                                        $translate(
                                            'placeholders.individualName'
                                        )
                                    "
                                    dense
                                    outlined
                                    hide-details="auto"
                                    @keydown="handleKeyPress"
                                    :autofocus="true"
                                />
                            </v-col>

                            <v-col
                                cols="12"
                                sm="4"
                                v-if="
                                    customer.type == 'company' || !customer.type
                                "
                            >
                                <CompanySearchAutocomplete
                                    :value="customer.legal_name"
                                    :error-messages="
                                        customer_saving_errors.legal_name
                                    "
                                    :label="$translate('labels.legal_name')"
                                    :placeholder="
                                        $translate('placeholders.legal_name')
                                    "
                                    @input="onLegalNameInput"
                                    @company-selected="
                                        onLegalNameCompanySelected
                                    "
                                    :dense="true"
                                    outlined
                                    @keydown="handleKeyPress"
                                />
                            </v-col>
                            <v-col cols="12" sm="4">
                                <v-select
                                    required
                                    class="flex-grow-0 w-sm-100"
                                    :label="$translate('labels.type')"
                                    :error-messages="
                                        customer_saving_errors.type
                                    "
                                    :items="typesChoices"
                                    :value="customer.type || 'company'"
                                    @input="
                                        customer.type = $event;
                                        $forceUpdate();
                                    "
                                    hide-details="auto"
                                    dense
                                    outlined
                                    @keydown="handleKeyPress"
                                />
                            </v-col>
                            <v-col cols="12" sm="4">
                                <v-text-field
                                    v-model="customer.code"
                                    :error-messages="
                                        customer_saving_errors.code
                                    "
                                    :label="$translate('codeLabel')"
                                    name="code"
                                    dense
                                    outlined
                                    hide-details="auto"
                                    @keydown="handleKeyPress"
                                >
                                    <template #label>
                                        <span
                                            >{{ $translate("codeLabel")
                                            }}<StitchHint
                                                :hint="
                                                    $translate(
                                                        'labels.codeAutomaticallyGenerated'
                                                    )
                                                "
                                        /></span>
                                    </template>
                                </v-text-field>
                            </v-col>
                            <v-col
                                cols="12"
                                sm="4"
                                v-if="
                                    customer.type == 'company' || !customer.type
                                "
                            >
                                <CompanySiren
                                    class="flex-grow-0 w-sm-100"
                                    v-model="customer.siren"
                                    :error-messages="sirenErrorMessages"
                                    :required="highlightMandatory"
                                    :label="$translate('labels.siren')"
                                    :placeholder="
                                        $translate('placeholders.siren')
                                    "
                                    @company-selected="onCompanySelected"
                                    :dense="true"
                                    outlined
                                    @keydown="handleKeyPress"
                                />
                            </v-col>

                            <v-col
                                cols="12"
                                sm="4"
                                v-if="
                                    customer.type == 'company' || !customer.type
                                "
                            >
                                <v-text-field
                                    class="flex-grow-0 w-sm-100"
                                    v-model="customer.vat_number"
                                    :error-messages="
                                        customer_saving_errors.vat_number
                                    "
                                    :label="$translate('labels.vatNumber')"
                                    name="vat_number"
                                    dense
                                    outlined
                                    hide-details="auto"
                                    @keydown="handleKeyPress"
                                />
                            </v-col>

                            <v-col
                                cols="12"
                                sm="4"
                                v-if="
                                    customer.type == 'company' || !customer.type
                                "
                            >
                                <v-select
                                    required
                                    class="flex-grow-0 w-sm-100"
                                    :label="
                                        $translate('labels.businessActivity')
                                    "
                                    :error-messages="
                                        customer_saving_errors.persona
                                    "
                                    :items="personasChoices"
                                    :value="customer.persona || 'company'"
                                    @input="
                                        customer.persona = $event;
                                        $forceUpdate();
                                    "
                                    hide-details="auto"
                                    dense
                                    outlined
                                    @keydown="handleKeyPress"
                                />
                            </v-col>
                            <v-col
                                cols="12"
                                :sm="
                                    customer.type == 'company' || !customer.type
                                        ? 8
                                        : 12
                                "
                            >
                                <TagInput
                                    object-type="customer"
                                    v-model="customer.tags"
                                    :label="$translate('labels.tags')"
                                    :placeholder="
                                        $translate('placeholders.tags')
                                    "
                                    :dense="true"
                                    outlined
                                    hide-details="auto"
                                    @keydown="handleKeyPress"
                                    :error-messages="
                                        customer_saving_errors.tags
                                    "
                                />
                            </v-col>
                            <template v-if="catalogs && isOperator">
                                <v-col cols="12" lg="6">
                                    <Select
                                        required
                                        class="flex-grow-0 w-sm-100"
                                        :label="$translate('labels.catalog')"
                                        :error-messages="
                                            customer_saving_errors.catalog
                                        "
                                        :options="
                                            catalogs.map((c) => ({
                                                text:
                                                    c.name +
                                                    (c.default
                                                        ? ` (${$translate(
                                                              'labels.defaultCatalog'
                                                          )})`
                                                        : ''),
                                                value: c.id,
                                            }))
                                        "
                                        :value="
                                            allowed_catalogs
                                                ? allowed_catalogs[0]
                                                : null
                                        "
                                        @input="
                                            updateAllowedCatalogs([$event]);
                                            $forceUpdate();
                                        "
                                        :dense="true"
                                        outlined
                                    />
                                </v-col>
                            </template>
                        </v-row>
                    </v-card-text>
                </v-card>

                <v-card class="stitch-modal__card" elevation="0">
                    <v-card-text>
                        <h5 class="stitch-modal__title">
                            <v-icon size="16">mdi-file-document-outline</v-icon>
                            <span>{{ $translate("billingInformation") }}</span>
                        </h5>
                        <v-row dense class="stitch-modal__fields">
                            <v-col
                                cols="12"
                                sm="4"
                                v-if="billingaccounts.length"
                            >
                                <Select
                                    required
                                    class="flex-grow-0 w-sm-100"
                                    :label="$translate('labels.billingAccount')"
                                    :error-messages="
                                        customer_saving_errors.billingaccount
                                    "
                                    :options="billingaccountsChoices"
                                    :value="customer.billingaccount"
                                    :loading="billingaccounts_loading"
                                    :disabled="billingaccounts_loading"
                                    @input="changeBillingAccountId($event)"
                                    :dense="true"
                                    outlined
                                />
                            </v-col>
                            <v-col cols="12" sm="4">
                                <v-text-field
                                    class="flex-grow-0 w-sm-100"
                                    :value="
                                        customer.force_vat_rate
                                            ? decimal(customer.force_vat_rate)
                                                  .times(100)
                                                  .toFixed(2)
                                            : null
                                    "
                                    @focusout="changeVatRate($event)"
                                    :error-messages="
                                        customer_saving_errors.force_vat_rate
                                    "
                                    :label="$translate('labels.vatRate')"
                                    :placeholder="
                                        $translate('placeholders.vatRate', {
                                            default: defaultVatRate,
                                        })
                                    "
                                    name="force_vat_rate"
                                    dense
                                    outlined
                                    hide-details="auto"
                                    @keydown="handleKeyPress"
                                />
                            </v-col>
                            <v-col cols="12" sm="4">
                                <v-text-field
                                    class="flex-grow-0 w-sm-100"
                                    :value="customer.default_payment_delay"
                                    @input="updateDefaultPaymentDelay"
                                    :error-messages="
                                        customer_saving_errors.default_payment_delay
                                    "
                                    :label="$translate('labels.paymentDelay')"
                                    name="default_payment_delay"
                                    dense
                                    outlined
                                    hide-details="auto"
                                    @keydown="handleKeyPress"
                                />
                            </v-col>
                        </v-row>
                    </v-card-text>
                </v-card>

                <v-card class="stitch-modal__card" elevation="0">
                    <v-card-text>
                        <h5 class="stitch-modal__title">
                            <v-icon size="16">mdi-credit-card-outline</v-icon>
                            <span>{{
                                $translate("paymentMethodSection")
                            }}</span>
                        </h5>
                        <v-row dense class="stitch-modal__fields">
                            <v-col cols="12" sm="4">
                                <v-select
                                    class="flex-grow-0 w-sm-100"
                                    :value="customer.preferred_payment_method"
                                    :items="paymentMethodChoices"
                                    :label="
                                        $translate(
                                            'labels.preferredPaymentMethod'
                                        )
                                    "
                                    :error-messages="
                                        customer_saving_errors.preferred_payment_method
                                    "
                                    dense
                                    outlined
                                    hide-details="auto"
                                    clearable
                                    @input="updatePreferredPaymentMethod"
                                />
                            </v-col>
                        </v-row>
                        <v-row dense class="stitch-modal__fields">
                            <v-col cols="12" sm="4">
                                <v-text-field
                                    :value="customer.debit_iban"
                                    :error-messages="
                                        customer_saving_errors.debit_iban
                                    "
                                    :label="$translate('labels.debitIban')"
                                    dense
                                    outlined
                                    hide-details="auto"
                                    @input="
                                        updateDebitField('debit_iban', $event)
                                    "
                                />
                            </v-col>
                            <v-col cols="12" sm="4">
                                <v-text-field
                                    :value="customer.debit_bic"
                                    :error-messages="
                                        customer_saving_errors.debit_bic
                                    "
                                    :label="$translate('labels.debitBic')"
                                    dense
                                    outlined
                                    hide-details="auto"
                                    @input="
                                        updateDebitField('debit_bic', $event)
                                    "
                                />
                            </v-col>
                            <v-col cols="12" sm="4">
                                <v-text-field
                                    :value="customer.debit_account_holder"
                                    :error-messages="
                                        customer_saving_errors.debit_account_holder
                                    "
                                    :label="
                                        $translate('labels.debitAccountHolder')
                                    "
                                    dense
                                    outlined
                                    hide-details="auto"
                                    @input="
                                        updateDebitField(
                                            'debit_account_holder',
                                            $event
                                        )
                                    "
                                />
                            </v-col>
                            <v-col
                                cols="12"
                                v-if="
                                    customer.preferred_payment_method ===
                                        'direct_debit' && !customer.debit_iban
                                "
                            >
                                <p class="text-caption mb-0">
                                    {{ $translate("debitIbanMissing") }}
                                </p>
                            </v-col>
                        </v-row>
                    </v-card-text>
                </v-card>

                <v-card class="stitch-modal__card" elevation="0">
                    <v-card-text>
                        <h5 class="stitch-modal__title">
                            <v-icon size="16">mdi-map-marker-outline</v-icon>
                            <span>{{
                                $translate("labels.billingAddress")
                            }}</span>
                        </h5>
                        <v-row dense class="stitch-modal__fields">
                            <v-col cols="12">
                                <AddressForm
                                    :title="''"
                                    :value="customer.billing_address"
                                    @input="updateBillingAddress($event)"
                                    :errors="billingAddressErrors"
                                    :highlight-mandatory="showRequiredFeedback"
                                    :show-required-indicators="
                                        showRequiredFeedback
                                    "
                                    outlined
                                    @keydown="handleKeyPress"
                                />
                            </v-col>
                            <v-col cols="12" v-if="isNew">
                                <h6
                                    class="stitch-modal__title stitch-modal__title--sub"
                                >
                                    <v-icon size="16"
                                        >mdi-truck-delivery-outline</v-icon
                                    >
                                    <span>{{
                                        $translate("labels.deliveryAddress")
                                    }}</span>
                                </h6>
                                <v-checkbox
                                    :label="
                                        $translate(
                                            'labels.deliveryAddressSameAsBilling'
                                        )
                                    "
                                    v-model="deliveryAddressSameAsBilling"
                                    dense
                                />

                                <AddressForm
                                    v-if="!deliveryAddressSameAsBilling"
                                    :value="customer.delivery_address || {}"
                                    @input="
                                        (value) =>
                                            (customer.delivery_address = value)
                                    "
                                    :errors="
                                        customer_saving_errors.delivery_address
                                    "
                                    outlined
                                />
                            </v-col>
                        </v-row>
                    </v-card-text>
                </v-card>

                <v-card
                    v-if="stocks && isOperator"
                    class="stitch-modal__card"
                    elevation="0"
                >
                    <v-card-text>
                        <h5 class="stitch-modal__title">
                            <v-icon size="16">mdi-warehouse</v-icon>
                            <span>{{ $translate("warehouses") }}</span>
                        </h5>
                        <p class="stitch-modal__hint">
                            {{ $translate("warehousesHint") }}
                        </p>
                        <div class="stitch-modal__stack">
                            <v-switch
                                v-for="stock in stocks"
                                :key="stock.id"
                                :input-value="isStockAllowed(stock)"
                                :label="stock.name"
                                dense
                                inset
                                hide-details
                                @change="toggleStock(stock)"
                            />
                        </div>
                    </v-card-text>
                </v-card>

                <v-card
                    ref="commentsSectionRef"
                    :class="{
                        'customer-modal-section-highlight':
                            highlightCommentsSection,
                    }"
                    class="stitch-modal__card customer-modal-section"
                    elevation="0"
                >
                    <v-card-text>
                        <h5 class="stitch-modal__title">
                            <v-icon size="16">mdi-text-box-outline</v-icon>
                            <span>{{
                                $translate("additionalInformation")
                            }}</span>
                        </h5>
                        <v-row>
                            <v-col cols="12">
                                <v-textarea
                                    v-model="customer.comments"
                                    :error-messages="
                                        customer_saving_errors.comments
                                    "
                                    :label="$translate('labels.comments')"
                                    :placeholder="
                                        $translate('placeholders.comments')
                                    "
                                    dense
                                    outlined
                                    hide-details="auto"
                                    @keydown="handleKeyPress"
                                />
                            </v-col>
                        </v-row>
                    </v-card-text>
                </v-card>
            </div>
        </template>
        <template slot="bottom">
            <v-btn
                text
                class="my-2 my-sm-4 w-100 w-sm-auto text-none"
                @click="cancelEditClicked"
            >
                {{ $translate("buttons.cancel") }}
            </v-btn>
            <v-btn
                color="primary"
                class="my-2 my-sm-4 w-100 w-sm-auto ml-sm-3 text-none"
                :disabled="customer_saving"
                :loading="customer_saving"
                @click="saveInformationClicked"
            >
                {{ $translate("buttons.saveInformations") }}
            </v-btn>
        </template>
    </ModalContent>
</template>

<script>
import Vue from "vue";
import { mapState, mapActions, mapMutations, mapGetters } from "vuex";
import CustomerCatalogs from "@/modals/customercatalogs";
import { hasAssignableCustomerCatalogs } from "@/utils/customer-catalog-assignment";
import InformationOffer from "@/modals/customer-information-offer.vue";
import InformationRequest from "@/modals/customer-information-request.vue";
import GlobalMixins from "@/mixins/global";
import Decimal from "decimal.js";
import CompanySearchAutocomplete from "@/components/common/company-search-autocomplete.vue";
import CompanySiren from "@/components/common/company-siren.vue";
import TagInput from "@/components/common/tag-input.vue";

export default {
    mixins: [GlobalMixins],
    components: {
        CompanySearchAutocomplete,
        CompanySiren,
        TagInput,
    },
    props: {
        params: {
            type: Object,
            required: true,
        },
    },
    computed: {
        ...mapState("editcustomer/stocks", ["stocks", "stocks_loaded"]),
        ...mapState("editcustomer/catalogs", ["catalogs", "catalogs_loaded"]),
        ...mapState("editcustomer", [
            "customer",
            "allowed_stocks",
            "allowed_catalogs",
            "customer_saving",
            "customer_saving_errors",
            "delivery_address_same_as_billing",
        ]),
        ...mapState("editcustomer/billingaccounts", [
            "billingaccounts",
            "billingaccounts_loading",
        ]),
        paymentMethodChoices() {
            const all = [
                { text: this.$translate("paymentMethods.unset"), value: null },
                { text: this.$translate("paymentMethods.transfer"), value: "transfer" },
                { text: this.$translate("paymentMethods.direct_debit"), value: "direct_debit" },
                { text: this.$translate("paymentMethods.bill_of_exchange"), value: "bill_of_exchange" },
                { text: this.$translate("paymentMethods.check"), value: "check" },
                { text: this.$translate("paymentMethods.card"), value: "card" },
                { text: this.$translate("paymentMethods.cash"), value: "cash" },
            ];
            const enabled = this.enabledPaymentMethodsForCustomer;
            const current = this.customer?.preferred_payment_method || null;
            return all.filter(
                (item) =>
                    item.value === null ||
                    enabled.includes(item.value) ||
                    item.value === current
            );
        },
        enabledPaymentMethodsForCustomer() {
            const account =
                this.billingaccounts?.find(
                    (item) => item.id === this.customer?.billingaccount
                ) || this.customer?.billingaccount_data;
            const enabled = account?.enabled_payment_methods;
            if (Array.isArray(enabled)) {
                return enabled;
            }
            return [
                "transfer",
                "direct_debit",
                "bill_of_exchange",
                "check",
                "card",
                "cash",
            ];
        },
        deliveryAddressSameAsBilling: {
            get() {
                return this.delivery_address_same_as_billing;
            },
            set(value) {
                this.updateDeliveryAddressSameAsBilling(value);
            },
        },

        defaultVatRate() {
            if (this.customer.billingaccount_data?.default_vat_rate) {
                return new Decimal(
                    this.customer.billingaccount_data.default_vat_rate
                ).times(100);
            }
            return "20";
        },
        isExternal() {
            return !!this.customer?.entity;
        },
        highlightMandatory() {
            return !!this.params.highlightMandatory;
        },
        /** Show live required-field feedback (invoice completion / after save attempt). */
        showRequiredFeedback() {
            return this.highlightMandatory || this.attemptedSave;
        },
        missingRequiredFields() {
            return this.collectMissingRequiredFields();
        },
        canSave() {
            return this.missingRequiredFields.length === 0;
        },
        nameErrorMessages() {
            const serverErrors = this.customer_saving_errors?.name;
            if (serverErrors?.length) {
                return serverErrors;
            }
            if (this.showRequiredFeedback && !this.customer?.name?.trim()) {
                return [this.$translate("thisFieldIsRequired")];
            }
            return null;
        },
        typesChoices() {
            return [
                {
                    text: Vue.prototype.$translate("customer.type.company"),
                    value: "company",
                },
                {
                    text: Vue.prototype.$translate("customer.type.individual"),
                    value: "individual",
                },
            ];
        },
        personasChoices() {
            // Sort by text except 'other' code that remains at last
            return this.$utils
                .getPersonasChoices()
                .sort((a, b) =>
                    a.value === "other" ? 1 : a.text.localeCompare(b.text)
                );
        },
        isNew() {
            return !this.customer?.id;
        },
        saveText() {
            if (this.isNew) {
                return this.$translate("buttons.createCustomer");
            }
            return this.$translate("buttons.saveInformations");
        },
        title() {
            return this.params.customer?.id
                ? this.$translate("messages.editCustomer")
                : this.$translate("messages.addCustomer");
        },
        sirenErrorMessages() {
            const serverErrors = this.customer_saving_errors?.siren;
            if (serverErrors?.length) {
                return serverErrors;
            }
            const isCompany =
                this.customer?.type === "company" || !this.customer?.type;
            if (
                this.showRequiredFeedback &&
                this.requiresBillingIdentity &&
                isCompany &&
                !this.customer?.siren
            ) {
                return [this.$translate("thisFieldIsRequired")];
            }
            return null;
        },
        requiresBillingIdentity() {
            // Same completeness rules as invoice sending (highlightMandatory).
            return this.highlightMandatory;
        },
        billingAddressErrors() {
            const serverErrors = this.customer_saving_errors?.billing_address || {};
            if (!this.showRequiredFeedback || !this.requiresBillingIdentity) {
                return serverErrors;
            }
            const address = this.customer?.billing_address || {};
            const requiredMessage = this.$translate("thisFieldIsRequired");
            const merged = { ...serverErrors };
            const ensure = (field, empty) => {
                if (!empty) {
                    return;
                }
                if (!merged[field]?.length) {
                    merged[field] = [requiredMessage];
                }
            };
            ensure("line1", !address.line1);
            ensure("city", !address.city);
            ensure("postal_code", !address.postal_code);
            ensure(
                "emails",
                !address.emails || !address.emails.length,
            );
            return merged;
        },
        billingaccountsChoices() {
            let defaultvalue = [
                {
                    text: this.$translate("labels.defaultBillingAccount"),
                    value: null,
                },
            ];

            if (!this.billingaccounts) {
                return defaultvalue;
            }
            return defaultvalue.concat(
                this.billingaccounts
                    .map((item) => {
                        return {
                            text:
                                item.name +
                                (item.default
                                    ? " " + this.$translate("labels.default")
                                    : ""),
                            value: item.id,
                        };
                    })
                    .sort((a, b) => {
                        if (a.text < b.text) {
                            return -1;
                        }
                        if (a.text > b.text) {
                            return 1;
                        }
                        return 0;
                    })
            );
        },
    },
    methods: {
        ...mapMutations("editcustomer", [
            "updateAllowedCatalogs",
            "updateAllowedStocks",
            "updateCustomer",
            "updateCustomerSavingErrors",
            "updateDeliveryAddressSameAsBilling",
        ]),
        ...mapActions("mcl", ["hideModal", "showModal"]),
        ...mapActions("editcustomer", [
            "saveCustomer",
            "init",
            "changeBillingAccountId",
        ]),
        ...mapMutations("editcustomer", ["updateBillingAddress"]),
        changeVatRate(event) {
            const vat_rate = event.target.value;
            if (vat_rate == "") {
                this.customer.force_vat_rate = null;
                return;
            } else {
                this.customer.force_vat_rate = new Decimal(vat_rate)
                    .dividedBy(100)
                    .toFixed(2);
            }
        },
        updateDefaultPaymentDelay(value) {
            // Convert empty string to null, otherwise parse as integer using Decimal
            let paymentDelay = null;
            if (value !== "" && value !== null && value !== undefined) {
                try {
                    paymentDelay = new Decimal(value).toNumber();
                } catch (e) {
                    // If parsing fails, keep as null
                    paymentDelay = null;
                }
            }
            
            this.updateCustomer({
                ...this.customer,
                default_payment_delay: paymentDelay,
            });
        },
        updatePreferredPaymentMethod(value) {
            this.updateCustomer({
                ...this.customer,
                preferred_payment_method: value || null,
            });
        },
        updateDebitField(key, value) {
            this.updateCustomer({
                ...this.customer,
                [key]: value,
            });
        },
        toggleStock(stock) {
            let allowed_stocks = this.allowed_stocks || [];
            if (!allowed_stocks) {
                allowed_stocks = [];
            }
            let index = allowed_stocks.indexOf(stock.id);
            if (index == -1) {
                allowed_stocks.push(stock.id);
            } else {
                allowed_stocks.splice(index, 1);
            }
            this.updateAllowedStocks(allowed_stocks);
        },

        isStockAllowed(stock) {
            if (!this.allowed_stocks) {
                return false;
            }
            return this.allowed_stocks.indexOf(stock.id) != -1;
        },
        cancelEditClicked() {
            this.hideModal();
        },
        scrollToCommentsAndHighlight() {
            const ref = this.$refs.commentsSectionRef;
            const el = ref && (ref.$el || ref);
            if (!el || typeof el.scrollIntoView !== "function") return;
            el.scrollIntoView({ behavior: "smooth", block: "center" });
            this.highlightCommentsSection = true;
            setTimeout(() => {
                this.highlightCommentsSection = false;
            }, 2500);
        },

        // Handle Enter key for automatic save
        handleKeyPress(event) {
            if (event.key === "Enter") {
                event.preventDefault();
                if (!this.customer_saving) {
                    this.saveInformationClicked();
                }
            }
        },

        collectMissingRequiredFields() {
            const missing = [];
            if (!this.customer?.name?.trim()) {
                missing.push("name");
            }
            if (!this.requiresBillingIdentity) {
                return missing;
            }
            const isCompany =
                this.customer?.type === "company" || !this.customer?.type;
            if (isCompany && !this.customer?.siren) {
                missing.push("siren");
            }
            const address = this.customer?.billing_address || {};
            if (!address.line1) {
                missing.push("billing_address.line1");
            }
            if (!address.city) {
                missing.push("billing_address.city");
            }
            if (!address.postal_code) {
                missing.push("billing_address.postal_code");
            }
            if (!address.emails?.length) {
                missing.push("billing_address.emails");
            }
            return missing;
        },

        buildClientValidationErrors() {
            const requiredMessage = this.$translate("thisFieldIsRequired");
            const errors = {};
            if (!this.customer?.name?.trim()) {
                errors.name = [requiredMessage];
            }
            if (!this.requiresBillingIdentity) {
                return errors;
            }
            const isCompany =
                this.customer?.type === "company" || !this.customer?.type;
            if (isCompany && !this.customer?.siren) {
                errors.siren = [requiredMessage];
            }
            const address = this.customer?.billing_address || {};
            const billingErrors = {};
            if (!address.line1) {
                billingErrors.line1 = [requiredMessage];
            }
            if (!address.city) {
                billingErrors.city = [requiredMessage];
            }
            if (!address.postal_code) {
                billingErrors.postal_code = [requiredMessage];
            }
            if (!address.emails?.length) {
                billingErrors.emails = [requiredMessage];
            }
            if (Object.keys(billingErrors).length) {
                errors.billing_address = billingErrors;
            }
            return errors;
        },

        // Handle company selection from SIREN search or autocomplete
        onCompanySelected(companyData) {
            const newBillingAddress =
                companyData.line1 ||
                companyData.line2 ||
                companyData.city ||
                companyData.postal_code ||
                companyData.country
                    ? {
                          ...this.customer.billing_address,
                          line1: companyData.line1 || "",
                          line2: companyData.line2 || "",
                          city: companyData.city || "",
                          postal_code: companyData.postal_code || "",
                          country: companyData.country || "France",
                      }
                    : this.customer.billing_address;

            this.updateCustomer({
                ...this.customer,
                name: companyData.name || "",
                legal_name: companyData.legal_name || "",
                siren: companyData.siren || "",
                vat_number: companyData.vat_number || "",
                share_capital: companyData.share_capital || "",
                legal_structure: companyData.legal_structure || "",
                rcs: companyData.rcs || "",
                naf_code: companyData.naf_code || "",
                billing_address: newBillingAddress,
            });
            this.$forceUpdate();
        },

        // Handle manual input for legal_name field
        onLegalNameInput(value) {
            this.updateCustomer({
                ...this.customer,
                legal_name: value,
            });
        },

        // Handle company selection for legal_name field (only fills legal_name)
        onLegalNameCompanySelected(companyData) {
            const newBillingAddress =
                companyData.line1 ||
                companyData.line2 ||
                companyData.city ||
                companyData.postal_code ||
                companyData.country
                    ? {
                          ...this.customer.billing_address,
                          line1: companyData.line1 || "",
                          line2: companyData.line2 || "",
                          city: companyData.city || "",
                          postal_code: companyData.postal_code || "",
                          country: companyData.country || "France",
                      }
                    : this.customer.billing_address;

            this.updateCustomer({
                ...this.customer,
                legal_name: companyData.name || "",
                siren: companyData.siren || "",
                vat_number: companyData.vat_number || "",
                share_capital: companyData.share_capital || "",
                legal_structure: companyData.legal_structure || "",
                rcs: companyData.rcs || "",
                naf_code: companyData.naf_code || "",
                billing_address: newBillingAddress,
            });
            this.$forceUpdate();
        },

        async saveInformationClicked() {
            window.setTimeout(this.doSaveInformation, 0);
        },
        async doSaveInformation() {
            this.attemptedSave = true;
            const errors = this.buildClientValidationErrors();

            if (Object.keys(errors).length) {
                this.updateCustomerSavingErrors(errors);
                return;
            }
            this.updateCustomerSavingErrors({});

            try {
                const response = await this.saveCustomer({
                    instance: this.customer,
                });
                if (response.status == 201) {
                    this.addAlert(this.$translate("customerCreated"));
                } else {
                    this.addAlert(this.$translate("customerUpdated"));
                }
                if (this.isNew && await hasAssignableCustomerCatalogs(
                    response.data.id,
                    this.userHasPermission("catalog.view_sellcatalog"),
                )) {
                    await this.showModal({
                        component: CustomerCatalogs,
                        params: {
                            customer: response.data,
                            assign_defaults: true,
                        },
                    });
                }
                if (response.status === 201 && !response.data.archived
                    && this.userHasPermission("core.change_customer")
                    && this.userHasPermission("core.view_customer")) {
                    const choice = await this.showModal({
                        component: InformationOffer,
                        persistent: false,
                        params: { customer: response.data },
                        width: 680,
                    });
                    if (choice === true) {
                        await this.showModal({
                            component: InformationRequest,
                            params: { customers: [response.data] },
                            width: 760,
                        });
                    }
                }
                await this.hideModal({ result: response.data });
            } catch (error) {
                this.$swal({
                    title: this.$translate("error"),
                    html: this.$utils.resultToSwalContent(this, error),
                    type: "error",
                });
            }
        },
    },
    data() {
        return {
            highlightCommentsSection: false,
            attemptedSave: false,
        };
    },
    created() {
        var customer = {
            ...(this.params.customer || {}),
            billing_address: this.params.customer?.billing_address || {},
        };

        this.init({ customer });
    },
    mounted() {
        if (this.params.scrollToSection === "comments") {
            this.$nextTick(() => {
                setTimeout(() => this.scrollToCommentsAndHighlight(), 150);
            });
        }
    },
    i18n: {
        messages: {
            en: {
                codeLabel: "Customer code",
                instructionsExternal:
                    "This customer is connected with you on Safetee. You can modify the information about it, but it will be overwritten if the provider modifies them in their space.",
                labels: {
                    name: "Name",
                    legal_name: "Legal name",
                    codeAutomaticallyGenerated:
                        "Code (automatically generated if not provided)",
                    type: "Type",
                    siren: "SIREN",
                    vatNumber: "VAT number",
                    businessActivity: "Business activity",
                    catalog: "Catalog",
                    defaultCatalog: "default catalog",
                    billingAccount: "Billing account",
                    paymentDelay: "Payment delay",
                    preferredPaymentMethod: "Preferred payment method",
                    debitIban: "Debit IBAN",
                    debitBic: "BIC",
                    debitAccountHolder: "Account holder",
                    billingAddress: "Billing address",
                    deliveryAddress: "Delivery address",
                    deliveryAddressInstructions:
                        "If not provided, the billing address will be used for deliveries.",
                    comments: "Comments",
                    tags: "Tags",
                    defaultBillingAccount: "Default billing account",
                    default: "(default)",
                    deliveryAddressSameAsBilling:
                        "Delivery address same as billing address",
                    vatRate: "VAT rate",
                },
                buttons: {
                    cancel: "Cancel",
                    createCustomer: "Create customer",
                    saveInformations: "Save information",
                },
                messages: {
                    editCustomer: "Edit a customer",
                    addCustomer: "Add a customer",
                },
                billingInformation: "Billing information",
                paymentMethodSection: "Payment method",
                debitIbanMissing: "Debit IBAN is not set.",
                paymentMethods: {
                    unset: "Not set",
                    transfer: "Wire transfer",
                    direct_debit: "Direct debit",
                    bill_of_exchange: "Accepted bill of exchange",
                    check: "Check",
                    card: "Card terminal",
                    cash: "Cash",
                },
                warehouses: "Warehouse(s)",
                warehousesHint:
                    "Select the warehouses available for this customer.",
                additionalInformation: "Additional information",
                thisFieldIsRequired: "This field is required",
                requiredFieldsLegend:
                    "Fields marked as required must be filled before saving.",
                customerCreated: "Customer created",
                customerUpdated: "Customer updated",
                error: "Error",
                customerConfiguration: "Customer configuration",
                customer: {
                    type: {
                        company: "Company",
                        individual: "Individual",
                    },
                },
                placeholders: {
                    vatRate: "VAT rate (default: {default}%)",
                    comments:
                        "Enter additional information about this customer...",
                    tags: "Add tags to categorize this customer...",
                    companyName: "Customer name...",
                    individualName: "Full name...",
                    siren: "Enter a SIREN (9 digits)...",
                    legal_name: "Legal name (if different from name)...",
                },
                hints: {
                    legal_name: "Legal name (if different from name)",
                },
            },
            fr: {
                codeLabel: "Code client",
                instructionsExternal:
                    "Ce client est connecté avec vous sur Safetee. Vous pouvez modifier les informations le concernant mais elles seront écrasées si le fournisseur les modifie dans son espace.",
                labels: {
                    name: "Nom",
                    legal_name: "Raison sociale",
                    codeAutomaticallyGenerated:
                        "Code (généré automatiquement si non fourni)",
                    type: "Type",
                    siren: "SIREN",
                    vatNumber: "Numéro de TVA",
                    businessActivity: "Activité",
                    catalog: "Catalogue",
                    defaultCatalog: "catalogue par défaut",
                    billingAccount: "Compte de facturation",
                    paymentDelay: "Délai de paiement",
                    preferredPaymentMethod: "Mode de règlement préféré",
                    debitIban: "IBAN de prélèvement",
                    debitBic: "BIC",
                    debitAccountHolder: "Titulaire du compte",
                    billingAddress: "Adresse de facturation",
                    deliveryAddress: "Adresse de livraison",
                    deliveryAddressInstructions:
                        "Si non fournie, l'adresse de facturation sera utilisée pour les livraisons.",
                    comments: "Commentaires",
                    tags: "Tags",
                    defaultBillingAccount: "Compte de facturation par défaut",
                    default: "(par défaut)",
                    deliveryAddressSameAsBilling:
                        "Adresse de livraison identique à l'adresse de facturation",
                    vatRate: "Taux de TVA",
                },
                buttons: {
                    cancel: "Annuler",
                    createCustomer: "Créer un client",
                    saveInformations: "Enregistrer les informations",
                },
                messages: {
                    editCustomer: "Modifier un client",
                    addCustomer: "Ajouter un client",
                },
                billingInformation: "Informations de facturation",
                paymentMethodSection: "Mode de règlement",
                debitIbanMissing: "IBAN de prélèvement non renseigné.",
                paymentMethods: {
                    unset: "Non renseigné",
                    transfer: "Virement",
                    direct_debit: "Prélèvement",
                    bill_of_exchange: "Effet accepté",
                    check: "Chèque",
                    card: "TPE",
                    cash: "Espèces",
                },
                warehouses: "Entrepôt(s)",
                warehousesHint:
                    "Sélectionnez les entrepôts disponibles pour ce client.",
                additionalInformation: "Informations supplémentaires",
                thisFieldIsRequired: "Ce champ est requis",
                requiredFieldsLegend:
                    "Les champs obligatoires doivent être renseignés avant d'enregistrer.",
                customerCreated: "Client créé",
                customerUpdated: "Client mis à jour",
                error: "Erreur",
                customerConfiguration: "Configuration du client",
                customer: {
                    type: {
                        company: "Entreprise",
                        individual: "Particulier",
                    },
                },
                placeholders: {
                    vatRate: "Taux de TVA (par défaut : {default}%)",
                    comments:
                        "Saisissez des informations supplémentaires sur ce client...",
                    tags: "Ajoutez des tags pour catégoriser ce client...",
                    companyName: "Nom du client...",
                    individualName: "Nom complet...",
                    siren: "Entrez un SIREN (9 chiffres)...",
                    legal_name: "Raison sociale (si différent du nom)...",
                },
                hints: {
                    legal_name: "Raison sociale (si différent du nom)",
                },
            },
        },
    },
};
</script>

<style lang="scss" scoped>

@import "@/assets/css/_stitch-form.scss";
@import "@/assets/css/variables.scss";


// Pappers suggestions must extend beyond this card and above the next section.
.customer-configuration-card {
    overflow: visible;
    position: relative;
    z-index: 1;
}

.customer-modal-section {
    transition: background-color 0.4s ease-out, box-shadow 0.4s ease-out;
}

.customer-modal-section-highlight {
    background-color: $color-primary-extralight !important;
    box-shadow: 0 0 0 2px $color-primary-light;
}

.stitch-modal__fields ::v-deep .col-sm-8 > .v-input,
.stitch-modal__fields ::v-deep .col-sm-8 > div {
    width: 100%;
    max-width: 100%;
}
</style>
